import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteProductDesignApi,
  deleteProductDesignFileApi,
  getAllProductDesignApi,
  getProductDesignApi,
  postProductDesignApi,
  postProductDesignFilesApi,
  postProductSizeDesignApi,
  updateProductDesignApi,
} from "../../api/ecommerce/ProductDesignApi";

const initialState = {
  productDesign: null,
  productDesigns: [],
  designFiles: [],
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  fileUploading: false,
  fileDeleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getProductDesign = createAsyncThunk(
  "productDesign/getProductDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getProductDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postProductDesign = createAsyncThunk(
  "productDesign/postProductDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postProductSizeDesign = createAsyncThunk(
  "productDesign/postProductSizeDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductSizeDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateProductDesign = createAsyncThunk(
  "productDesign/updateProductDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteProductDesign = createAsyncThunk(
  "productDesign/deleteProductDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getAllProductDesign = createAsyncThunk(
  "productDesign/getAllProductDesign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAllProductDesignApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postProductDesignFiles = createAsyncThunk(
  "productDesign/postProductDesignFiles",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductDesignFilesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteProductDesignFile = createAsyncThunk(
  "productDesign/deleteProductDesignFile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductDesignFileApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const productDesignSlice = createSlice({
  name: "productDesign",
  initialState,
  reducers: {
    clearProductDesignError: (state) => {
      state.error = null;
    },
    clearProductDesignState: (state) => {
      state.productDesign = null;
      state.productDesigns = [];
      state.designFiles = [];
      state.loading = false;
      state.detailsLoading = false;
      state.saving = false;
      state.deleting = false;
      state.fileUploading = false;
      state.fileDeleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductDesign.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getProductDesign.fulfilled, (state, action) => {
        state.productDesign = action?.payload?.response ?? action?.payload;
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getProductDesign.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(getAllProductDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllProductDesign.fulfilled, (state, action) => {
        state.productDesigns = action?.payload?.response ?? action?.payload ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllProductDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postProductDesign.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postProductDesign.fulfilled, (state, action) => {
        state.productDesign = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postProductDesign.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postProductSizeDesign.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postProductSizeDesign.fulfilled, (state, action) => {
        state.productDesign = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postProductSizeDesign.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateProductDesign.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProductDesign.fulfilled, (state, action) => {
        state.productDesign = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateProductDesign.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteProductDesign.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProductDesign.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteProductDesign.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postProductDesignFiles.pending, (state) => {
        state.fileUploading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postProductDesignFiles.fulfilled, (state, action) => {
        state.designFiles = action?.payload?.response ?? action?.payload ?? [];
        state.fileUploading = false;
        state.success = true;
      })
      .addCase(postProductDesignFiles.rejected, (state, action) => {
        state.fileUploading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteProductDesignFile.pending, (state) => {
        state.fileDeleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProductDesignFile.fulfilled, (state) => {
        state.fileDeleting = false;
        state.success = true;
      })
      .addCase(deleteProductDesignFile.rejected, (state, action) => {
        state.fileDeleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const { clearProductDesignError, clearProductDesignState } =
  productDesignSlice.actions;

export default productDesignSlice.reducer;
